import { ArrowRight, Check, ChevronDown, Clock3, Sun } from "lucide-react";
import { RANGEMENT_FAQ, RANGEMENT_OFFERS, RANGEMENT_PHOTOS, RANGEMENT_SERVICE, RANGEMENT_STEPS } from "../../lib/content";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

const OFFER_ICONS = [Clock3, Sun];

const book = () => window.dispatchEvent(new CustomEvent("cs:preselect-service", { detail: RANGEMENT_SERVICE }));

const Photos = () => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="rangement-photos">
    <img src={RANGEMENT_PHOTOS[0].src} alt={RANGEMENT_PHOTOS[0].alt} loading="lazy" className="col-span-2 h-64 w-full rounded-xl object-cover sm:h-80" />
    {RANGEMENT_PHOTOS.slice(1).map((p) => <img key={p.src} src={p.src} alt={p.alt} loading="lazy" className="h-44 w-full rounded-xl object-cover sm:h-56" />)}
  </div>
);

const Steps = () => (
  <ol className="mt-16 grid gap-6 md:grid-cols-3" data-testid="rangement-steps">
    {RANGEMENT_STEPS.map((s, i) => (
      <Reveal as="li" key={s.title} delay={i * 0.08} className="relative rounded-xl border border-cs-navy/10 bg-white p-7">
        <span className="font-display text-4xl font-extrabold text-cs-yellow">{i + 1}</span>
        <h3 className="mt-3 font-display text-lg font-bold tracking-tight text-cs-navy">{s.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-cs-ink/75">{s.text}</p>
      </Reveal>
    ))}
  </ol>
);

const Offers = () => (
  <div className="mt-6 grid gap-6 md:grid-cols-2" data-testid="rangement-offers">
    {RANGEMENT_OFFERS.map((o, i) => {
      const Icon = OFFER_ICONS[i];
      return (
        <Reveal key={o.title} delay={i * 0.08} className="flex gap-5 rounded-xl bg-cs-navy p-7 text-white" data-testid={`rangement-offer-${i + 1}`}>
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-cs-yellow text-cs-navy"><Icon className="h-6 w-6" /></span>
          <div>
            <h3 className="font-display text-lg font-bold">{o.title}</h3>
            <p className="mt-1 text-[15px] leading-relaxed text-white/75">{o.text}</p>
          </div>
        </Reveal>
      );
    })}
  </div>
);

const Faq = () => (
  <div className="mt-16 max-w-3xl" data-testid="rangement-faq">
    <h3 className="font-display text-2xl font-bold tracking-tight text-cs-navy">Questions fréquentes</h3>
    <div className="mt-5 divide-y divide-cs-navy/10 rounded-xl border border-cs-navy/10 bg-white">
      {RANGEMENT_FAQ.map((f, i) => (
        <details key={f.q} className="group px-6 py-5" data-testid={`rangement-faq-${i + 1}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-cs-navy">
            {f.q}<ChevronDown className="h-5 w-5 shrink-0 transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <p className="mt-3 text-[15px] leading-relaxed text-cs-ink/75">{f.a}</p>
        </details>
      ))}
    </div>
  </div>
);

export const Rangement = () => (
  <section id="rangement" data-testid="rangement-section" className="scroll-mt-20 bg-cs-grey py-20 md:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <span className="eyebrow">Nouveau · Pour les particuliers</span>
          <h2 className="section-title">Rangement à domicile</h2>
          <p className="mt-5 font-display text-xl font-bold text-cs-navy" data-testid="rangement-tagline">« Fini le cheni… à la maison aussi. »</p>
          <p className="mt-4 text-[16px] leading-relaxed text-cs-ink/80">
            Pas le temps, plus l'énergie ou simplement besoin d'un coup de main ? On trie avec vous, on range, on organise : désencombrement,
            dressing, cuisine, cave ou garage, préparation d'un déménagement ou d'un emménagement. Avec discrétion et bienveillance, sans jugement.
          </p>
          <ul className="mt-6 space-y-2 text-[15px] text-cs-ink/80">
            {["Visite gratuite et sans engagement", "Avec vous ou sans vous, selon vos consignes", "Objets donnés, recyclés ou évacués"].map((t) => (
              <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-cs-yellow-dark" strokeWidth={3} /> {t}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/reserver-visite" className="btn-accent px-6 py-3.5" data-testid="rangement-cta-button">
              Réserver une visite gratuite <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#devis" onClick={book} className="font-bold text-cs-navy underline decoration-cs-yellow decoration-2 underline-offset-4" data-testid="rangement-callback-link">Je préfère être rappelé</a>
          </div>
        </Reveal>
        <Reveal delay={0.1}><Photos /></Reveal>
      </div>
      <h3 className="mt-20 font-display text-2xl font-bold tracking-tight text-cs-navy">Comment ça marche</h3>
      <Steps />
      <Offers />
      <Faq />
    </div>
  </section>
);
